import React from "react";
import styled from "styled-components";
import PollDisplay from "../../components/poll-display";
import {
  Heart,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Bookmark,
  ImageIcon,
  FileText,
  ListChecks,
  MapPin,
  Home as HomeIcon,
  Search,
  Bell,
  Pencil,
  User as UserIcon,
} from "lucide-react";
import { router } from "@inertiajs/react";
import LeftSidebar from "../../components/left-sidebar";
import TrendingPanel from "../../components/trending-panel";
import CreateThreadModal from "../../components/create-thread-modal";
import {
  BackgroundTheme,
  AppLayout,
  MainArea,
  FeedColumn,
  RightSidebar,
  MobileHeader,
  MobileAvatar,
  MobileTitle,
  FeedTabs,
  FeedTab,
  ComposeBox,
  ComposeAvatar,
  ComposeContent,
  ComposeInput,
  ComposeActions,
  ComposeTools,
  ComposeTool,
  ComposeButton,
  ActionButton,
  RoleBadge,
} from "../../constants/styles";

import { LoggedInUserContext } from "../../../providers/ProvideLoggedInUser";
import { apiGet, apiPost, apiDelete } from "../../../api";
import { renderContent } from "../../utils/render-content";

// ---- Skeleton for auth loading ----
const SkeletonPulse = styled.div`
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  animation: pulse 1.5s ease-in-out infinite;
  background: var(--secondary);
  border-radius: 0.375rem;
`;

const ThreadCardSkeleton = () => (
  <div style={{ padding: "1rem", borderBottom: "1px solid var(--border)" }}>
    <div style={{ display: "flex", gap: "0.75rem" }}>
      <SkeletonPulse style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <SkeletonPulse style={{ height: 14, width: "40%", marginBottom: "0.5rem" }} />
        <SkeletonPulse style={{ height: 14, width: "90%", marginBottom: "0.375rem" }} />
        <SkeletonPulse style={{ height: 14, width: "70%" }} />
      </div>
    </div>
  </div>
);

// Components
const ThreadCard = ({ thread, onLike, onBookmark, onRepost, onDelete, onPollUpdate, currentUserId }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [menuOpen]);

  const handleCardClick = (e) => {
    if (e.target.closest("button") || e.target.closest("a")) return;
    router.visit(`/communities/threads/${thread.id}`);
  };

  const handleCopyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/communities/threads/${thread.id}`);
    setMenuOpen(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDelete(thread.id);
  };

  const isOwn = currentUserId && thread.author?.id === currentUserId;

  return (
    <Article onClick={handleCardClick}>
      <ThreadContent>
        <a
          href={`/communities/profiles/${thread.author.slug || thread.author.handle.replace("@", "")}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Avatar>
            {thread.author.avatar_url ? (
              <img
                src={thread.author.avatar_url}
                alt={thread.author.name}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <AvatarText>
                {thread.author.name?.charAt(0)}
              </AvatarText>
            )}
          </Avatar>
        </a>
        <MainThreadContent>
          <ThreadHeader>
            <AuthorLine>
              <a
                href={`/communities/profiles/${thread.author.slug || thread.author.handle.replace("@", "")}`}
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: "none" }}
              >
                <Name>{thread.author.name}</Name>
              </a>
              {thread.author.role && (
                <RoleBadge>{thread.author.role.split(" ")[0]}</RoleBadge>
              )}
              <Handle>{thread.author.handle}</Handle>
              <Dot>·</Dot>
              <Timestamp title={thread.created_at_iso ? new Date(thread.created_at_iso).toLocaleString() : undefined}>{thread.timestamp}</Timestamp>
            </AuthorLine>
            <div style={{ position: "relative" }} ref={menuRef}>
              <MoreButton onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}>
                <MoreHorizontal size={18} />
              </MoreButton>
              {menuOpen && (
                <ThreadMenu>
                  <ThreadMenuItem onClick={handleCopyLink}>
                    Copy link to post
                  </ThreadMenuItem>
                  {isOwn && (
                    <ThreadMenuItem $danger onClick={handleDelete}>
                      Delete post
                    </ThreadMenuItem>
                  )}
                </ThreadMenu>
              )}
            </div>
          </ThreadHeader>
          <TextContent>{renderContent(thread.content)}</TextContent>
          {thread.media_urls?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
              {thread.media_urls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  style={{ maxHeight: 200, maxWidth: "100%", borderRadius: "0.5rem", objectFit: "cover", cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); window.open(url, "_blank"); }}
                />
              ))}
            </div>
          )}
          {thread.poll && (
            <PollDisplay
              poll={thread.poll}
              threadId={thread.id}
              compact
              authorId={thread.author?.id}
              onPollUpdate={onPollUpdate}
            />
          )}
          <Actions>
            <ActionButton onClick={(e) => { e.stopPropagation(); router.visit(`/communities/threads/${thread.id}`); }}>
              <MessageCircle size={18} />
              {thread.replies > 0 && <span>{thread.replies}</span>}
            </ActionButton>
            <ActionButton
              $isActive={thread.reposted}
              onClick={(e) => { e.stopPropagation(); onRepost(); }}
            >
              <Share2 size={18} />
              {thread.reposts > 0 && <span>{thread.reposts}</span>}
            </ActionButton>
            <ActionButton
              $isActive={thread.liked}
              $activeColor="#ef4444"
              onClick={(e) => { e.stopPropagation(); onLike(); }}
            >
              <Heart size={18} fill={thread.liked ? "#ef4444" : "none"} />
              {thread.likes > 0 && <span>{thread.likes}</span>}
            </ActionButton>
            <ActionButton
              $isActive={thread.bookmarked}
              onClick={(e) => { e.stopPropagation(); onBookmark(); }}
            >
              <Bookmark
                size={18}
                fill={thread.bookmarked ? "currentColor" : "none"}
              />
            </ActionButton>
          </Actions>
        </MainThreadContent>
      </ThreadContent>
    </Article>
  );
};

const UnnamedComponent = () => {
  const { signedIn, loading: authLoading, currentUser } = React.use(LoggedInUserContext);
  const [showCreateThread, setShowCreateThread] = React.useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  // Start empty — only populate from API when signed in, or mock data when guest
  const [threads, setThreads] = React.useState([]);
  const [threadsLoading, setThreadsLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("forYou");
  const [activeTopic, setActiveTopic] = React.useState(null);
  const sentinelRef = React.useRef(null);

  const loadThreads = React.useCallback((filter, page = 1, topic = null) => {
    if (page === 1) setThreadsLoading(true); else setLoadingMore(true);
    let base = "/api/v1/communities/threads";
    const params = new URLSearchParams();
    if (filter === "following") params.set("filter", "following");
    if (topic) params.set("topic", topic);
    params.set("page", page);
    params.set("perpage", 20);
    const url = `${base}?${params}`;
    apiGet(url)
      .then((res) => {
        if (res.parsedResponse?.data) {
          if (page === 1) setThreads(res.parsedResponse.data);
          else setThreads((prev) => [...prev, ...res.parsedResponse.data]);
          const meta = res.parsedResponse.meta;
          setCurrentPage(meta?.page ?? page);
          setHasMore(meta ? meta.page < meta.pages : false);
        }
      })
      .finally(() => { setThreadsLoading(false); setLoadingMore(false); });
  }, []);

  React.useEffect(() => {
    // Wait for auth check to complete before deciding what to show
    if (authLoading) return;
    if (!signedIn) {
      // Guest: show mock data (AuthOverlay will blur/gate it)
      setThreads([]);
      return;
    }
    // Authenticated: always load real data
    loadThreads(activeTab === "following" ? "following" : null, 1, activeTopic);
  }, [signedIn, authLoading]);

  // Poll for new threads every 30 seconds (silent background refresh)
  React.useEffect(() => {
    if (!signedIn) return;
    const interval = setInterval(() => {
      const filter = activeTabRef.current === "following" ? "following" : null;
      const url = filter === "following"
        ? "/api/v1/communities/threads?filter=following&page=1&perpage=20"
        : "/api/v1/communities/threads?page=1&perpage=20";
      apiGet(url).then((res) => {
        if (res.parsedResponse?.data) {
          setThreads((prev) => {
            const existingIds = new Set(prev.map((t) => t.id));
            const newThreads = res.parsedResponse.data.filter((t) => !existingIds.has(t.id));
            return newThreads.length > 0 ? [...newThreads, ...prev] : prev;
          });
        }
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [signedIn]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (signedIn) loadThreads(tab === "following" ? "following" : null, 1);
  };

  // Optimistic update helper
  const updateThread = (threadId, changes) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === threadId ? { ...t, ...changes } : t))
    );
  };

  const toggleLike = async (thread) => {
    const wasLiked = thread.liked;
    updateThread(thread.id, {
      liked: !wasLiked,
      likes: wasLiked ? thread.likes - 1 : thread.likes + 1,
    });
    try {
      if (wasLiked) {
        await apiDelete(`/api/v1/communities/threads/${thread.id}/likes`);
      } else {
        await apiPost(`/api/v1/communities/threads/${thread.id}/likes`);
      }
    } catch {
      updateThread(thread.id, { liked: wasLiked, likes: thread.likes });
    }
  };

  const toggleBookmark = async (thread) => {
    const wasBookmarked = thread.bookmarked;
    updateThread(thread.id, { bookmarked: !wasBookmarked });
    try {
      if (wasBookmarked) {
        await apiDelete(`/api/v1/communities/threads/${thread.id}/bookmarks`);
      } else {
        await apiPost(`/api/v1/communities/threads/${thread.id}/bookmarks`);
      }
    } catch {
      updateThread(thread.id, { bookmarked: wasBookmarked });
    }
  };

  const toggleRepost = async (thread) => {
    const wasReposted = thread.reposted;
    updateThread(thread.id, {
      reposted: !wasReposted,
      reposts: wasReposted ? thread.reposts - 1 : thread.reposts + 1,
    });
    try {
      if (wasReposted) {
        await apiDelete(`/api/v1/communities/threads/${thread.id}/reposts`);
      } else {
        await apiPost(`/api/v1/communities/threads/${thread.id}/reposts`);
      }
    } catch {
      updateThread(thread.id, { reposted: wasReposted, reposts: thread.reposts });
    }
  };

  const handleThreadCreated = (newThread) => {
    setThreads((prev) => [newThread, ...prev]);
  };

  const handleDelete = async (threadId) => {
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    try {
      await apiDelete(`/api/v1/communities/threads/${threadId}`);
    } catch {
      loadThreads(activeTab === "following" ? "following" : null, 1);
    }
  };

  // Infinite scroll via IntersectionObserver
  const activeTabRef = React.useRef(activeTab);
  activeTabRef.current = activeTab;
  React.useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !threadsLoading) {
          loadThreads(activeTabRef.current === "following" ? "following" : null, currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, threadsLoading, currentPage, loadThreads]);

  const userInitial = currentUser?.name?.charAt(0) || currentUser?.username?.charAt(0) || "?";
  const userHandle = currentUser?.username || "";

  return (
    <AppLayout>
      <LeftSidebar
        onCreatePost={() => setShowCreateThread(true)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <MainArea>
        <FeedColumn>
          {/* Mobile Header */}
          <MobileHeader>
            <MobileAvatar onClick={() => setMobileSidebarOpen(true)}>
              <span>{userInitial}</span>
            </MobileAvatar>
            <MobileTitle>Home</MobileTitle>
          </MobileHeader>

          {/* Feed Tabs */}
          <FeedTabs>
            <FeedTab
              $active={activeTab === "forYou"}
              onClick={() => handleTabChange("forYou")}
            >
              For you
            </FeedTab>
            <FeedTab
              $active={activeTab === "following"}
              onClick={() => handleTabChange("following")}
            >
              Following
            </FeedTab>
          </FeedTabs>

          {/* Compose Box */}
          <ComposeBox>
            <ComposeAvatar>
              <span>{userInitial}</span>
            </ComposeAvatar>
            <ComposeContent>
              <ComposeInput onClick={() => setShowCreateThread(true)}>
                What{"'"}s happening?
              </ComposeInput>
              <ComposeActions>
                <ComposeTools>
                  <ComposeTool onClick={() => setShowCreateThread(true)} title="Add image">
                    <ImageIcon size={20} />
                  </ComposeTool>
                  <ComposeTool onClick={() => setShowCreateThread(true)} title="Attach document">
                    <FileText size={20} />
                  </ComposeTool>
                  <ComposeTool onClick={() => setShowCreateThread(true)} title="Add checklist">
                    <ListChecks size={20} />
                  </ComposeTool>
                  <ComposeTool onClick={() => setShowCreateThread(true)} title="Add location">
                    <MapPin size={20} />
                  </ComposeTool>
                </ComposeTools>
                <ComposeButton onClick={() => setShowCreateThread(true)}>
                  Post
                </ComposeButton>
              </ComposeActions>
            </ComposeContent>
          </ComposeBox>

          {/* Feed */}
          {authLoading ? (
            <>
              <ThreadCardSkeleton />
              <ThreadCardSkeleton />
              <ThreadCardSkeleton />
            </>
          ) : threadsLoading ? (
            <>
              <ThreadCardSkeleton />
              <ThreadCardSkeleton />
              <ThreadCardSkeleton />
            </>
          ) : threads.length === 0 ? (
            <div
              style={{
                padding: "3rem 2rem",
                textAlign: "center",
                color: "var(--muted-foreground)",
              }}
            >
              {activeTab === "following"
                ? "Follow people to see their posts here."
                : "No posts yet. Be the first to share!"}
            </div>
          ) : (
            threads.map((thread) => (
              <ThreadCard
                key={thread.id}
                thread={thread}
                onLike={() => toggleLike(thread)}
                onBookmark={() => toggleBookmark(thread)}
                onRepost={() => toggleRepost(thread)}
                onDelete={handleDelete}
                onPollUpdate={(updatedPoll) => setThreads((prev) => prev.map((t) => t.id === thread.id ? { ...t, poll: updatedPoll } : t))}
                currentUserId={currentUser?.id}
              />
            ))
          )}

          {/* Infinite scroll sentinel */}
          {signedIn && (
            <>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ textAlign: "center", padding: "1rem", color: "var(--muted-foreground)", fontSize: "0.875rem" }}>
                  Loading more...
                </div>
              )}
              {!hasMore && threads.length > 0 && (
                <div style={{ textAlign: "center", padding: "1rem", color: "var(--muted-foreground)", fontSize: "0.8125rem" }}>
                  You{"'"}re all caught up!
                </div>
              )}
            </>
          )}

          {/* Spacer for mobile bottom nav */}
          <div style={{ height: "80px" }} />
        </FeedColumn>

        {/* Right Sidebar */}
        <RightSidebar>
          <TrendingPanel onCategoryClick={(slug) => {
            setActiveTopic(slug);
            setActiveTab("forYou");
            loadThreads(null, 1, slug);
          }} />
        </RightSidebar>
      </MainArea>

      {/* Floating Post Button (Mobile) */}
      <FloatingPostButton onClick={() => setShowCreateThread(true)}>
        <Pencil size={24} />
      </FloatingPostButton>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav>
        <NavItem href="/communities" $active>
          <HomeIcon size={26} />
        </NavItem>
        <NavItem href="/communities/explore">
          <Search size={26} />
        </NavItem>
        <NavItem href="/communities/notifications">
          <Bell size={26} />
        </NavItem>
        <NavItem href="/communities/bookmarks">
          <Bookmark size={26} />
        </NavItem>
        <NavItem href={userHandle ? `/communities/profiles/${userHandle}` : "/communities"}>
          <UserIcon size={26} />
        </NavItem>
      </MobileBottomNav>

      {showCreateThread && (
        <CreateThreadModal
          onClose={() => setShowCreateThread(false)}
          onCreated={handleThreadCreated}
        />
      )}
    </AppLayout>
  );
};

export default UnnamedComponent;

// Thread Card Styles
const Article = styled.article`
  border-bottom: 1px solid var(--border);
  padding: 1rem;
  background-color: var(--card);
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: var(--secondary);
  }
`;

const ThreadContent = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarText = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: white;
`;

const MainThreadContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ThreadHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const AuthorLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const Name = styled.span`
  font-weight: 700;
  color: var(--foreground);
`;

const Handle = styled.span`
  color: var(--muted-foreground);
  font-size: 0.9375rem;
`;

const Dot = styled.span`
  color: var(--muted-foreground);
`;

const Timestamp = styled.span`
  color: var(--muted-foreground);
  font-size: 0.9375rem;
`;

const MoreButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  opacity: 0;
  transition: all 0.2s;

  ${Article}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(var(--primary-rgb), 0.1);
    color: var(--primary);
  }
`;

const TextContent = styled.div`
  margin-top: 0.25rem;
  font-size: 0.9375rem;
  color: var(--foreground);
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Actions = styled.div`
  margin-top: 0.75rem;
  display: flex;
  justify-content: space-between;
  max-width: 425px;
  margin-left: -0.5rem;
`;

const ThreadMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 200;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  overflow: hidden;
`;

const ThreadMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.9375rem;
  font-weight: ${(props) => (props.$danger ? "700" : "400")};
  color: ${(props) => (props.$danger ? "#ef4444" : "var(--foreground)")};
  transition: background 0.15s;

  &:hover {
    background: var(--secondary);
  }
`;

// Mobile Bottom Nav
const MobileBottomNav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card);
  border-top: 1px solid var(--border);
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 100;

  @media (min-width: 720px) {
    display: none;
  }
`;

const NavItem = styled.a`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.$active ? "var(--foreground)" : "var(--muted-foreground)"};
  position: relative;
  text-decoration: none;

  &:hover {
    background: var(--secondary);
  }
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;

  &:hover {
    background: var(--secondary);
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 18px;
  height: 18px;
  background: var(--primary);
  color: white;
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const FloatingPostButton = styled.button`
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 50;

  @media (min-width: 720px) {
    display: none;
  }

  &:hover {
    opacity: 0.9;
  }
`;
